
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, Link } from 'react-router-dom';

const DataDeletion = () => {
  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setLoading(true);
    
    try {
      // Here you would typically call an API to process the deletion request
      // For now, we'll simulate the request
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('Data deletion request submitted successfully. You will receive a confirmation email within 24 hours.');
      setEmail('');
      setReason('');
    } catch (error) {
      toast.error('Failed to submit deletion request. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="text-center mb-8">
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 mb-4 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="w-8 h-8 bg-gradient-to-br from-green-600 to-emerald-700 rounded-sm rotate-45 flex items-center justify-center">
              <span className="text-white font-bold text-sm rotate-[-45deg]">S</span>
            </div>
            <span className="text-2xl font-bold text-green-600 tracking-tight hover:text-green-700 transition-colors">SocialScoop.ai</span>
          </Link>
          <h1 className="text-3xl font-bold text-foreground mb-2">Data Deletion Request</h1>
          <p className="text-muted-foreground">Request to delete your personal data from SocialScoop.ai</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              <span>Delete My Data</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div className="text-red-800 text-sm">
                  <p className="font-semibold mb-1">Important Notice:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>This action cannot be undone</li>
                    <li>All your account data will be permanently deleted</li>
                    <li>Connected social media accounts will be disconnected</li>
                    <li>Processing may take up to 30 days</li>
                  </ul>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter the email associated with your account"
                  required
                />
                <p className="text-sm text-muted-foreground mt-1">
                  This must match the email address used for your SocialScoop.ai account
                </p>
              </div>

              <div>
                <Label htmlFor="reason">Reason for Deletion (Optional)</Label>
                <Textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Let us know why you're deleting your account (optional)"
                  rows={3}
                />
              </div>

              <Button 
                type="submit" 
                disabled={loading}
                className="w-full bg-red-600 hover:bg-red-700"
              >
                {loading ? 'Processing...' : 'Submit Deletion Request'}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-2">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-2">
                If you have questions about data deletion or need assistance:
              </p>
              <a 
                href="mailto:privacy@socialscoop.ai"
                className="text-primary hover:underline text-sm"
              >
                Contact our Privacy Team: privacy@socialscoop.ai
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DataDeletion;
